import { useState, useEffect } from 'react';
import logo from '../../assets/logo.png';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginUser } from '../../api/identity.ts';
import Cookies from 'js-cookie';
import { onClickUrl } from '../../utils/openinNewTab.tsx';
import { getUserById } from '../../api/user.ts';
import GoogleLogin from '../../components/GoogleLogin';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const togglePasswordVisibility = () =>
    setIsPasswordVisible(!isPasswordVisible);

  const [failedLogin, setFailedLogin] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setFailedLogin(false);
    setErrorMessage('');

    try {
      const data = await loginUser(email, password);
      const user = await getUserById();

      if (!user || !user.id) throw new Error('User not found');

      const onboarded = user.onboarded;

      localStorage.setItem('user', JSON.stringify(user));

      if (!onboarded) {
        navigate('/onboarding');
      } else {
        navigate(`/global/settings`);
      }
    } catch (error: any) {
      console.log(error);
      if (error.status === 401 && error.type === 'UnauthorizedException') {
        console.log('Unauthorized');
        navigate('/login', { state: { message: 'Please log in again.' } });
      } else if (error.status === 403) {
        console.log('bye');
        navigate('/createAccount', { state: { email } });
      } else if (error.status === 500) {
        navigate('/onboarding');
      } else {
        setFailedLogin(true);
        setErrorMessage('Email or password is incorrect');
      }
    }
  };

  useEffect(() => {
    const userIsLogged = Cookies.get('accessToken');

    if (userIsLogged) {
      navigate('/global/settings');
    }
  }, [navigate]);

  const handleGoogleError = (error: string) => {
    setFailedLogin(true);
    setErrorMessage(error);
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen relative">
      <div className="flex flex-col items-center bg-grey rounded-3xl p-8 w-[370px] md:w-[500px] md:m-4">
        <a href="https://superpower.social/" className="inline-block">
          <img
            src={logo}
            className="h-8 mb-8 cursor-pointer"
            alt="Superpower Social Logo - Go to Homepage"
          />
        </a>
        <h1 className="text-xl mb-8">Login</h1>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col w-full md:px-4">
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white bg-opacity-5 rounded-3xl px-4 py-2 mb-4"
              placeholder="Email address"
              type="text"
            />
            <div className="flex flex-row items-center bg-white bg-opacity-5 rounded-3xl">
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="flex-grow bg-white bg-opacity-0 rounded-3xl px-4 py-2"
                placeholder="Password"
                type={isPasswordVisible ? 'text' : 'password'}
              />
              {isPasswordVisible ? (
                <EyeIcon
                  className="w-6 h-6 mx-4 cursor-pointer"
                  onClick={togglePasswordVisibility}
                />
              ) : (
                <EyeSlashIcon
                  className="w-6 h-6 mx-4 cursor-pointer"
                  onClick={togglePasswordVisibility}
                />
              )}
            </div>

            <div className="flex flex-row justify-center">
              {failedLogin && (
                <span className="text-xs text-red mt-4">
                  {errorMessage || 'Email or password is incorrect'}
                </span>
              )}
            </div>
            <button
              className={`rounded-3xl p-2 mt-8 
            ${
              email && password
                ? 'bg-yellow text-black hover:brightness-90'
                : 'bg-white text-black'
            }`}
              type="submit"
              onClick={handleSubmit}
            >
              Login
            </button>
          </div>
        </form>

        <div className="w-full mt-4 px-4">
          <div className="flex items-center mb-4">
            <div className="h-px bg-white bg-opacity-20 flex-grow"></div>
            <span className="px-4 text-white text-opacity-60 text-sm">or</span>
            <div className="h-px bg-white bg-opacity-20 flex-grow"></div>
          </div>

          <div className="h-[46px] flex justify-center">
            <GoogleLogin mode="login" onError={handleGoogleError} />
          </div>
        </div>

        <div className="flex flex-row justify-center mt-8">
          <button
            className="hover:brightness-90"
            onClick={() => navigate('/requestResetPassword')}
          >{`Reset Password >`}</button>
        </div>
      </div>
      <div className="mt-4 sm:text-sm text-xs">
        <span>
          {`Don't have an account yet?`}
          <button onClick={() => navigate('/createAccount')}>
            <span className="text-yellow hover:brightness-90 ml-2">
              Create Account Now {`>`}
            </span>
          </button>
        </span>
      </div>
      <div className="absolute bottom-0 pb-4">
        <span>
          <button
            className="hover:text-yellow"
            onClick={onClickUrl('https://www.superpower.social/privacy-policy')}
          >
            Privacy
          </button>
          {` and `}
          <button
            className="hover:text-yellow"
            onClick={onClickUrl(
              'https://www.superpower.social/terms-of-service'
            )}
          >
            Terms of Service
          </button>
        </span>
      </div>
    </div>
  );
};

export default Login;
